import React, { useMemo } from 'react';
import qrcode from 'qrcode-generator';

/**
 * QRコードを SVG で描く。
 *
 * 画像化(canvas → PNG)ではなく SVG にしているのは、**印刷したときに滲まないため**。
 * 打刻用QRは紙に印刷して従業員に配る運用があり(設計書 §13-4)、
 * 小さく印刷しても読み取れる必要がある。
 *
 * 誤り訂正レベルは **M**(約15%復元)。紙が多少汚れても読めて、
 * かつ H ほどセルが細かくならない——現場で配る紙としての折り合い。
 */
interface Props {
  /** QRに入れる文字列 */
  value: string;
  /**
   * 一辺の表示サイズ。数値は px。
   * **印刷は物理単位で渡すこと**(例 `'40mm'`)——px 指定だと紙の上の大きさが
   * ブラウザの解像度換算に依存し、切り取ったカードの寸法が揃わない。
   */
  size?: number | string;
  /** 余白(セル数)。QR規格の推奨は4。小さくすると読み取り率が落ちる */
  margin?: number;
  title?: string;
}

const QrCode: React.FC<Props> = ({ value, size = 200, margin = 4, title }) => {
  // value が変わらない限り作り直さない。印刷ビューでは何十個も並ぶため
  const svg = useMemo(() => {
    if (!value) return null;
    // 第1引数 0 = 型番自動(データ量に応じて最小の型を選ぶ)
    const qr = qrcode(0, 'M');
    qr.addData(value);
    qr.make();
    return qr.createSvgTag({ cellSize: 1, margin, scalable: true });
  }, [value, margin]);

  if (!svg) return null;

  return (
    <div
      role="img"
      aria-label={title ?? 'QRコード'}
      style={{
        width: size,
        height: size,
        // scalable の SVG は viewBox で伸縮する。枠に合わせて描かせる
        display: 'block',
        background: '#fff',
      }}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
};

export default QrCode;
